import React, { useState, useEffect } from 'react';
import {
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as LocalShippingIcon,
  Assessment as AssessmentIcon,
  Star as StarIcon,
  Dashboard as DashboardIcon,
  SmartToy as SmartToyIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { Supplier, SupplierMetrics, PurchaseOrder } from '../types';
import supplierService from '../services/supplierService';
import { SupplierIntelligenceDashboard } from './SupplierIntelligenceDashboard';
import { SmartPurchaseOrderWizard } from './SmartPurchaseOrderWizard';

export const SupplierDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SupplierMetrics | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [sortField, setSortField] = useState<'CompanyName' | 'LastOrderDate'>('CompanyName');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [metricsData, suppliersData, ordersData] = await Promise.all([
        supplierService.getSupplierMetrics(),
        supplierService.getSuppliers({}),
        supplierService.getPurchaseOrders({})
      ]);
      
      setMetrics(metricsData);
      setSuppliers(suppliersData?.suppliers || []);
      setPurchaseOrders(ordersData || []);
    } catch (error) {
      console.error('Error loading supplier dashboard:', error);
      setError('Failed to load supplier data. Using mock data for development.');
      // Use mock data if API fails
      setMetrics(getMockSupplierMetrics());
      setSuppliers(getMockSuppliers());
      setPurchaseOrders(getMockPurchaseOrders());
    } finally {
      setLoading(false);
    }
  };

  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDetailModalOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSort = (field: 'CompanyName' | 'LastOrderDate') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedSuppliers = () => {
    let filteredSuppliers = suppliers;
    
    // Filter out archived suppliers unless showInactive is true
    if (!showInactive) {
      filteredSuppliers = suppliers.filter(supplier => 
        !supplier.CompanyName?.startsWith('Archive')
      );
    }
    
    // Apply search filter
    if (searchTerm) {
      filteredSuppliers = filteredSuppliers.filter(supplier => 
        supplier.CompanyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.City?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.State?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort suppliers
    return filteredSuppliers.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      if (sortField === 'LastOrderDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 75) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Supplier Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
        >
          Add Supplier
        </Button>
      </Box>

      {/* Error and Success Messages */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="supplier management tabs">
          <Tab 
            icon={<DashboardIcon />} 
            label="Intelligence Dashboard" 
            iconPosition="start"
          />
          <Tab 
            icon={<SmartToyIcon />} 
            label="Smart PO Wizard" 
            iconPosition="start"
          />
          <Tab 
            icon={<BusinessIcon />} 
            label="Supplier Directory" 
            iconPosition="start"
          />
          <Tab 
            icon={<TimelineIcon />} 
            label="Purchase Orders" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <SupplierIntelligenceDashboard />
      )}

      {activeTab === 1 && (
        <SmartPurchaseOrderWizard />
      )}

      {activeTab === 2 && (
        <Box>
          {/* Metrics Cards */}
          {metrics && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BusinessIcon color="primary" sx={{ mr: 1 }} />
                      <Typography color="textSecondary" gutterBottom>
                        Total Suppliers
                      </Typography>
                    </Box>
                    <Typography variant="h4">
                      {formatNumber(metrics.totalSuppliers)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {metrics.activeSuppliers} active
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocalShippingIcon color="warning" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Pending Orders
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatNumber(metrics.pendingOrders)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ${formatCurrency(metrics.totalSpendThisMonth)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    Pending Deliveries
                  </Typography>
                                  <Typography variant="h4">
                    {formatNumber(metrics.overdueOrders)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {metrics.averageLeadTime} days avg
                  </Typography>
              </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AssessmentIcon color="success" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" gutterBottom>
                    This Month Spend
                  </Typography>
                                  <Typography variant="h4">
                    {formatCurrency(metrics.totalSpendThisMonth)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    ${formatCurrency(metrics.totalSpendThisYear)} this year
                  </Typography>
              </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search suppliers by name, category, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Suppliers List */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Suppliers ({getSortedSuppliers().length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    color="primary"
                  />
                }
                label="Show Inactive"
              />
            </Box>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('CompanyName')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Supplier
                      {sortField === 'CompanyName' && (
                        <Typography variant="caption" color="primary">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Performance Score</TableCell>
                  <TableCell align="right">Active POs</TableCell>
                  <TableCell 
                    align="right"
                    onClick={() => handleSort('LastOrderDate')}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                      Last Order
                      {sortField === 'LastOrderDate' && (
                        <Typography variant="caption" color="primary">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedSuppliers().map((supplier) => (
                  <TableRow key={supplier.SupplierID} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {supplier.CompanyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {supplier.SupplierID}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {supplier.City && supplier.State ? `${supplier.City}, ${supplier.State}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        icon={<StarIcon />}
                        label={`${supplier.Rating || 0}/5`}
                        color={getPerformanceColor((supplier.Rating || 0) * 20)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {purchaseOrders.filter(po => po.SupplierID === supplier.SupplierID && po.Status === 'PENDING').length}
                    </TableCell>
                    <TableCell align="right">
                      {supplier.LastOrderDate ? new Date(supplier.LastOrderDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewSupplier(supplier)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Recent Purchase Orders */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Purchase Orders
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.slice(0, 10).map((po) => (
                  <TableRow key={po.PurchaseOrderID} hover>
                    <TableCell>{po.OrderNumber}</TableCell>
                    <TableCell>
                      {suppliers.find(s => s.SupplierID === po.SupplierID)?.CompanyName || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={po.Status}
                        color={po.Status === 'PENDING' ? 'primary' : po.Status === 'SHIPPED' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(po.TotalAmount)}</TableCell>
                    <TableCell>{new Date(po.OrderDate).toLocaleDateString()}</TableCell>
                    <TableCell>{po.ExpectedDeliveryDate ? new Date(po.ExpectedDeliveryDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined">
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Supplier Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            {selectedSupplier?.CompanyName} - Supplier Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedSupplier && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Contact Information</Typography>
                <Typography variant="body1">{selectedSupplier.ContactName || 'N/A'}</Typography>
                <Typography variant="body2">{selectedSupplier.Email}</Typography>
                <Typography variant="body2">{selectedSupplier.Phone || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Performance</Typography>
                <Typography variant="h4" color={getPerformanceColor((selectedSupplier.Rating || 0) * 20)}>
                  {selectedSupplier.Rating || 0}/5
                </Typography>
                <Typography variant="body2">Rating</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">
                  {selectedSupplier.Address || 'N/A'}, {selectedSupplier.City || 'N/A'}, {selectedSupplier.State || 'N/A'} {selectedSupplier.Country || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )}

  {activeTab === 3 && (
    <Box>
      {/* Purchase Orders Management */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Purchase Orders Management
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Value</TableCell>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((po) => (
                  <TableRow key={po.PurchaseOrderID} hover>
                    <TableCell>{po.OrderNumber}</TableCell>
                    <TableCell>
                      {suppliers.find(s => s.SupplierID === po.SupplierID)?.CompanyName || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={po.Status}
                        color={po.Status === 'PENDING' ? 'primary' : po.Status === 'SHIPPED' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{formatCurrency(po.TotalAmount)}</TableCell>
                    <TableCell>{new Date(po.OrderDate).toLocaleDateString()}</TableCell>
                    <TableCell>{po.ExpectedDeliveryDate ? new Date(po.ExpectedDeliveryDate).toLocaleDateString() : '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" variant="outlined">
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  )}
    </Box>
  );
};

// Mock data for development
function getMockSupplierMetrics(): SupplierMetrics {
  return {
    totalSuppliers: 24,
    activeSuppliers: 22,
    totalSpendThisMonth: 45200,
    totalSpendThisYear: 245000,
    averageLeadTime: 14,
    averageOnTimeDelivery: 87,
    topSuppliersBySpend: [
      { supplier: 'Beauty Supply Co.', amount: 12500 },
      { supplier: 'Hair Products Inc.', amount: 8900 }
    ],
    pendingOrders: 7,
    overdueOrders: 3,
    supplierRiskScore: 12
  };
}

function getMockSuppliers(): Supplier[] {
  return [
    {
      SupplierID: 'SUP-001',
      CompanyName: 'Beauty Supply Co.',
      ContactName: 'John Smith',
      Email: 'john@beautysupply.com',
      Phone: '(555) 123-4567',
      Address: '123 Main St',
      City: 'Los Angeles',
      State: 'CA',
      Country: 'USA',
      Status: 'ACTIVE',
      DateAdded: new Date('2024-01-01'),
      Rating: 4.8,
      OnTimeDeliveryRate: 95,
      QualityScore: 92,
      LastOrderDate: new Date('2024-01-15')
    },
    {
      SupplierID: 'SUP-002',
      CompanyName: 'Hair Products Inc.',
      ContactName: 'Sarah Johnson',
      Email: 'sarah@hairproducts.com',
      Phone: '(555) 234-5678',
      Address: '456 Oak Ave',
      City: 'New York',
      State: 'NY',
      Country: 'USA',
      Status: 'ACTIVE',
      DateAdded: new Date('2024-01-01'),
      Rating: 4.3,
      OnTimeDeliveryRate: 87,
      QualityScore: 89,
      LastOrderDate: new Date('2024-01-10')
    },
    {
      SupplierID: 'SUP-003',
      CompanyName: 'Archive-Old Supplier Co.',
      ContactName: 'Archive Contact',
      Email: 'archive@oldsupplier.com',
      Phone: '(555) 999-9999',
      Address: '999 Archive St',
      City: 'Archive City',
      State: 'AR',
      Country: 'USA',
      Status: 'INACTIVE',
      DateAdded: new Date('2023-01-01'),
      Rating: 3.5,
      OnTimeDeliveryRate: 75,
      QualityScore: 70,
      LastOrderDate: new Date('2023-06-15')
    }
  ];
}

function getMockPurchaseOrders(): PurchaseOrder[] {
  return [
    {
      PurchaseOrderID: 'PO-001',
      SupplierID: 'SUP-001',
      SupplierName: 'Beauty Supply Co.',
      OrderNumber: 'PO-2024-001',
      OrderDate: new Date('2024-01-15'),
      ExpectedDeliveryDate: new Date('2024-01-29'),
      Status: 'PENDING',
      SubTotal: 12500,
      TotalAmount: 12500,
      Currency: 'USD',
      PaymentStatus: 'PENDING',
      ItemCount: 5,
      CreatedBy: 'admin',
      CreatedDate: new Date('2024-01-15'),
      ModifiedDate: new Date('2024-01-15')
    },
    {
      PurchaseOrderID: 'PO-002',
      SupplierID: 'SUP-002',
      SupplierName: 'Hair Products Inc.',
      OrderNumber: 'PO-2024-002',
      OrderDate: new Date('2024-01-12'),
      ExpectedDeliveryDate: new Date('2024-01-26'),
      Status: 'SHIPPED',
      SubTotal: 8900,
      TotalAmount: 8900,
      Currency: 'USD',
      PaymentStatus: 'PENDING',
      ItemCount: 3,
      CreatedBy: 'admin',
      CreatedDate: new Date('2024-01-12'),
      ModifiedDate: new Date('2024-01-12')
    }
  ];
}
